import useNotification from 'antd/es/notification/useNotification';
import {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseFormGetValues } from 'react-hook-form';
import { RefFormHandler } from '.';
import Form from '../../../components/Form';
import { axiosInstance } from '../../../services/axios-instance';
import { Order, OrderStatus } from '../../../services/orders-service/types';
import useAlertStore from '../../../store/alert';
import { useOrderStore } from '../../../store/order-store';
import useSaveOrderStore from '../../../store/save-order-store';
import StringUtils, { errorMessage, jsonToCSV } from '../../../utils';
import useStatusForm from './useStatusForm';
import { useAuthStore } from '../../../store/auth-store';

/**
 * StatusForm props
 */
interface Props {
  order: Order;
  onUploadChange?: (name: string | undefined | null) => void;
}

/**
 * StatusForm Component
 */
const StatusForm: ForwardRefRenderFunction<RefFormHandler, Props> = (
  { order, onUploadChange },
  ref,
) => {
  const { refreshSession } = useAuthStore();
  const [notification, notifContext] = useNotification();
  const { updateStatus, updateDetails, getDetailedOrder } = useOrderStore();
  const saveOrderStore = useSaveOrderStore();
  const { alert } = useAlertStore();
  const formRef = useRef<RefFormHandler>(null);
  const [uploadName, setUploadName] = useState<string | null>(null);
  const [secondFormError, setSecondFormError] = useState<string | null>(null);
  const {
    fields,
    secondFormFields,
    setUploadSuccess,
    setUploadError,
    isButtonDisabled,
    isOrderDetailsButtonDisabled,
    filledOutByLabel,
  } = useStatusForm({ order, form: formRef.current });

  useEffect(() => {
    if (onUploadChange) {
      onUploadChange(uploadName);
    }
  }, [onUploadChange, uploadName]);

  useImperativeHandle(ref, () => ({
    onSubmit: async data => {
      await formRef.current?.onSubmit(data);
    },
    getValues: formRef.current?.getValues as UseFormGetValues<
      Record<string, string>
    >,
  }));

  const releaseStatus = useMemo(
    () =>
      [OrderStatus.RELEASE_APPROVED, OrderStatus.RELEASE_REJECTED].includes(
        order.status,
      ),
    [order.status],
  );

  const convertCSVtoUTF8File = async (file: File): Promise<File> => {
    // Try UTF-8 first
    let utf8File = await tryConvertFileToUTF8(file, 'utf-8');
    if (!(await isValidUTF8File(utf8File))) {
      console.log('Detected non-UTF-8 encoding; attempting ISO-8859-1');

      // Try ISO-8859-1 if UTF-8 fails
      utf8File = await tryConvertFileToUTF8(file, 'iso-8859-1');
      if (!(await isValidUTF8File(utf8File))) {
        console.log(
          'Detected non-ISO-8859-1 encoding; attempting Windows-1252',
        );

        // Try Windows-1252 if ISO-8859-1 also fails
        utf8File = await tryConvertFileToUTF8(file, 'windows-1252');
      }
    }

    return utf8File;
  };

  // Helper function to read file as UTF-8 or another encoding
  const tryConvertFileToUTF8 = (file, encoding) => {
    return new Promise<File>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(file, encoding);

      reader.onload = () => {
        const content = reader.result as string;
        const utf8Content = new TextEncoder().encode(content);
        const utf8File = new File([utf8Content], file.name, {
          type: 'text/csv;charset=utf-8',
        });
        resolve(utf8File);
      };

      reader.onerror = () => reject(reader.error);
    });
  };

  // Check if the file content is valid UTF-8 (look for replacement characters like �)
  const isValidUTF8File = file => {
    return new Promise<boolean>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(file);

      reader.onload = () => {
        const content = reader.result as string;
        resolve(!content.includes('�'));
      };

      reader.onerror = () => reject(reader.error);
    });
  };

  const uploadFile = useCallback(
    async ([file]: FileList) => {
      setUploadSuccess(null);
      setUploadError(null);
      setUploadName(null);
      if (!file) {
        return;
      }
      const utf8File = await convertCSVtoUTF8File(file);

      const formData = new FormData();
      formData.append('file', utf8File); // Append the UTF-8 encoded file

      try {
        await axiosInstance({
          method: 'POST',
          params: { orderId: order.id },
          url: '/lab-results/uploadCsv',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setUploadName(file.name);
        setUploadSuccess('Upload CSV Success');
      } catch (err) {
        setUploadError(errorMessage(err));
        refreshSession();
        throw err;
      }
    },
    [order.id, refreshSession, setUploadError, setUploadSuccess],
  );

  const onSubmit = useCallback(
    async (data: any) => {
      alert(
        'Confirmation',
        `Are you sure you want to change the status to <strong>${StringUtils.capitalize(
          data?.status?.toLowerCase(),
        )}</strong>?
        \n
        <i>Note: This step is irreversible and may trigger sending an email notification to this customer.</i>`,
        [
          { text: 'No', style: { color: '#003E74', backgroundColor: '#ccc' } },
          {
            text: 'Yes',
            onClick: async () => {
              const { file, ...cData }: any & { file: FileList } = {
                ...order,
                ...data,
              };
              // eslint-disable-next-line no-console
              console.log(file);
              await updateStatus(cData, data.status);
              await getDetailedOrder(order.id);
              await notification.success({
                message: 'Order status successfully changed.',
              });
            },
          },
        ],
      );
    },
    [alert, getDetailedOrder, notification, order, updateStatus],
  );

  const defaultValues = useMemo(() => {
    const data = saveOrderStore[order.id]?.data || {};
    if (order.testKit) {
      if (data.fileJson?.fileName) {
        const contents = jsonToCSV(data.fileJson?.data);
        const blob = new Blob([contents], { type: 'text/csv' });
        const file = new File([blob], data.fileJson?.fileName, {
          type: 'text/csv',
        });
        data.file = [file];
      }

      return {
        ...data,
        orderRejectedBy: `${order.testKit?.approvingPhysicianFirstName || ''} ${
          order.testKit?.approvingPhysicianLastName || ''
        }`,
        orderApprovedBy: `${order.testKit?.approvingPhysicianFirstName || ''} ${
          order.testKit?.approvingPhysicianLastName || ''
        }`,
        releaseRejectedBy: `${
          order.testKit?.approvingPhysicianFirstName || ''
        } ${order.testKit?.approvingPhysicianLastName || ''}`,
        sampleCollectionDate:
          data.sampleCollectionDate || order?.testKit?.sampleCollectionDate,
        fastingStatus: data.fastingStatus || order?.testKit?.fastingStatus,
        note: data.note || order?.testKit?.note,
      };
    }
    return data;
  }, [order.id, order.testKit, saveOrderStore]);

  const onSubmitOrderDetails = useCallback(
    (data: any) => {
      alert(
        'Confirmation',
        `Are you sure you want to submit these order details?
      \n
        <i>Note: This step is irreversible and thus, you won't be able to modify the submitted details.</i>`,
        [
          { text: 'No', style: { color: '#003E74', backgroundColor: '#ccc' } },
          {
            text: 'Yes',
            onClick: async () => {
              setSecondFormError(null);
              try {
                const { file, ...cData } = data;
                if (file && !order.labResult?.s3Key) {
                  await uploadFile(file);
                }
                await updateDetails({
                  ...cData,
                  testKit: order.testKit,
                  id: order.id,
                  status: OrderStatus.SPECIMEN_RECEIVED,
                });
                notification.success({
                  message: 'Order details successfully submitted.',
                });
              } catch (err) {
                setSecondFormError(errorMessage(err));
                const elem = document.querySelector('#second-form-error');
                if (elem) {
                  elem.scrollIntoView(true);
                }
              }
            },
          },
        ],
      );
    },
    [
      alert,
      notification,
      order.id,
      order.labResult?.s3Key,
      order.testKit,
      updateDetails,
      uploadFile,
    ],
  );

  return (
    <div className="bg-[#F8F8F8] -m-[24px] rounded-br-lg px-[32px] py-[27px] border-t-4 border-blue flex-1 flex flex-col">
      {notifContext}
      {!releaseStatus ? (
        <h5 className="font-base text-[27px]">
          To be filled out by a{' '}
          <b className="font-semibold">{filledOutByLabel}</b>
        </h5>
      ) : null}
      <div className="mt-[57px]">
        <Form
          formProps={{ onSubmitCapture: onSubmit }}
          fields={fields}
          submitButtonProps={{
            label: 'Submit Order Status',
            style: { alignSelf: 'flex-end' },
            disabled: isButtonDisabled,
          }}
          defaultValues={defaultValues}
          hasSubmitButton={!releaseStatus}
        />
        {order.status === OrderStatus.SPECIMEN_RECEIVED ||
        order.status === OrderStatus.COMPLETED ||
        order.status === OrderStatus.RELEASE_REJECTED ? (
          <>
            <div className="my-5">
              <hr />
            </div>
            <div id="second-form-error">
              {secondFormError ? (
                <span className="text-[#f00]">{secondFormError}</span>
              ) : null}
            </div>
            <Form
              ref={formRef}
              formProps={{ onSubmitCapture: onSubmitOrderDetails }}
              fields={secondFormFields}
              submitButtonProps={{
                label: 'Submit Order Details',
                style: { alignSelf: 'flex-end' },
                disabled: isOrderDetailsButtonDisabled,
              }}
              defaultValues={defaultValues}
              className="mt-2"
              hasSubmitButton={
                order.status !== OrderStatus.COMPLETED &&
                order.status !== OrderStatus.RELEASE_REJECTED
              }
            />
          </>
        ) : null}
      </div>
    </div>
  );
};

export default forwardRef(StatusForm);
